import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { FolderOpen, CheckCircle2, Award } from 'lucide-react';

const PortfolioSection = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      badge: 'RÉALISATIONS',
      title: 'Notre savoir-faire',
      subtitle: 'Typologies d\'ouvrages maîtrisées',
      items: [
        'Charpentes métalliques pour bâtiments industriels et entrepôts',
        'Structures métalliques pour ateliers, hangars et plateformes techniques',
        'Ouvrages métalliques secondaires : escaliers, garde-corps, passerelles',
        'Renforcement et réhabilitation de structures métalliques existantes',
        'Fabrication et montage de structures métalliques sur mesure'
      ],
      quality: {
        title: 'Approche Qualité',
        items: [
          'Respect des normes techniques applicables',
          'Contrôle qualité rigoureux',
          'Priorité à la sécurité et à la conformité'
        ]
      }
    },
    en: {
      badge: 'PROJECTS',
      title: 'Our expertise',
      subtitle: 'Types of structures mastered',
      items: [
        'Metal frameworks for industrial buildings and warehouses',
        'Metal structures for workshops, hangars and technical platforms',
        'Secondary metal works: stairs, railings, walkways',
        'Reinforcement and rehabilitation of existing metal structures',
        'Custom manufacturing and assembly of metal structures'
      ],
      quality: {
        title: 'Quality Approach',
        items: [
          'Compliance with applicable technical standards',
          'Rigorous quality control',
          'Priority on safety and compliance'
        ]
      }
    }
  };

  const t = content[language];

  const images = [
    'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80',
    'https://images.pexels.com/photos/53176/architecture-iron-steel-building-53176.jpeg?w=600',
    'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=600&q=80',
  ];

  return (
    <section id="portfolio" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A227]/10 border border-[#C9A227]/20 rounded-full mb-6">
            <FolderOpen className="w-4 h-4 text-[#C9A227]" />
            <span className="text-[#C9A227] font-semibold text-sm">{t.badge}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#1A1A1A] mb-4">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600">{t.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left - Portfolio items */}
          <div className="space-y-4">
            {t.items.map((item, index) => (
              <div
                key={index}
                className="flex items-start gap-4 bg-white rounded-xl p-5 border border-gray-100 hover:border-[#C9A227]/30 transition-colors"
              >
                <div className="w-10 h-10 bg-[#C9A227] rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">{String(index + 1).padStart(2, '0')}</span>
                </div>
                <p className="text-gray-700 pt-2">{item}</p>
              </div>
            ))}

            {/* Quality box */}
            <div className="bg-[#1A1A1A] rounded-xl p-6 mt-6">
              <div className="flex items-center gap-3 mb-4">
                <Award className="w-6 h-6 text-[#C9A227]" />
                <h3 className="text-lg font-bold text-white">{t.quality.title}</h3>
              </div>
              <div className="space-y-3">
                {t.quality.items.map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 text-[#C9A227] flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Images */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-4">
              <div className="h-48 rounded-xl overflow-hidden">
                <img src={images[0]} alt="Project" className="w-full h-full object-cover" />
              </div>
              <div className="h-64 rounded-xl overflow-hidden">
                <img src={images[1]} alt="Project" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="space-y-4 pt-8">
              <div className="h-64 rounded-xl overflow-hidden">
                <img src={images[2]} alt="Project" className="w-full h-full object-cover" />
              </div>
              <div className="h-48 bg-[#C9A227] rounded-xl flex items-center justify-center">
                <div className="text-center text-white">
                  <div className="text-4xl font-bold">100+</div>
                  <div className="text-sm opacity-90">{language === 'fr' ? 'Projets réalisés' : 'Completed projects'}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PortfolioSection;